"use client";

import { useEffect } from "react";
import { usePostHog } from "@posthog/next";

import { useConsent } from "@/components/consent-provider";
import { webAnalyticsSession } from "@/lib/briefpanel-analytics-storage";

type Client = NonNullable<ReturnType<typeof usePostHog>>;

// The `bp_vid` PostHog already took as its distinct id in this browser. A
// sign-out `reset()` also clears PostHog's own consent flag, so the next page
// load looks like a first consent again; this keeps the adoption to once, so a
// second account on the same browser does not inherit the first one's person.
const ADOPTED_VISITOR_KEY = "bp_posthog_vid";

/**
 * Applies cookie-consent transitions to the running PostHog client.
 *
 * The provider initializes with `opt_out_capturing_by_default: true` (see
 * provider.tsx), which only applies when posthog-js has no PERSISTED choice:
 * `opt_in_capturing()`/`opt_out_capturing()` persist the user's decision, so a
 * returning "all" visitor starts capturing immediately — including the first
 * pageview — while new/necessary visitors stay opted out until they accept.
 *
 * On the first "all", the site takes `bp_vid` as its anonymous distinct id —
 * the id the app bootstraps with — so the site visit and the app session land
 * on one PostHog person (#2916).
 */
export function ConsentSubscriber() {
  const posthog = usePostHog();
  const { consent } = useConsent();

  useEffect(() => {
    if (!posthog) {
      return;
    }
    if (consent === "necessary") {
      posthog.opt_out_capturing();
      posthog.reset();
      return;
    }
    if (consent !== "all") {
      return;
    }
    if (posthog.has_opted_in_capturing()) {
      posthog.opt_in_capturing();
      return;
    }
    // Leaving cookieless mode, the SDK mints a random id and wipes the super
    // properties; skip its `$opt_in` and send ours once the identity settles.
    posthog.opt_in_capturing({ captureEventName: null });
    void settleIdentity(posthog);
  }, [consent, posthog]);

  return null;
}

/**
 * Takes `bp_vid` as the distinct id (once per browser), restores the campaign
 * super properties the opt-in reset wiped, and re-captures the landing
 * pageview, which went out under the cookieless identity before the accept.
 */
async function settleIdentity(posthog: Client) {
  const search = window.location.search;
  const [visitorId, campaign] = await Promise.all([
    webAnalyticsSession.resolveVisitorId(search),
    webAnalyticsSession.resolveCampaignProps(search),
  ]);
  if (readAdoptedVisitorId() !== visitorId) {
    posthog.register({ distinct_id: visitorId, $device_id: visitorId });
    writeAdoptedVisitorId(visitorId);
  }
  if (Object.keys(campaign).length > 0) {
    posthog.register_once(campaign);
  }
  posthog.capture("$opt_in", undefined, { send_instantly: true });
  posthog.capture("$pageview");
}

function readAdoptedVisitorId() {
  try {
    return window.localStorage.getItem(ADOPTED_VISITOR_KEY);
  } catch {
    return null;
  }
}

function writeAdoptedVisitorId(visitorId: string) {
  try {
    window.localStorage.setItem(ADOPTED_VISITOR_KEY, visitorId);
  } catch {
    // No storage (private mode): the adoption may repeat on the next consent.
  }
}
